/* eslint-disable no-unused-vars */
import CardAccordion from 'components/atoms/CardAccordion';
import {
  IClusters,
  IStatusDataChart,
} from 'components/molecules/ChartInspectionStatus/types';
import ChartItemsAmountStock from 'components/molecules/ChartItemsAmount';
import ChartStatusStock from 'components/molecules/ChartStockStatusValidity';
import { useSelectFilter } from 'hooks/planning/useFilter';
import { IClusterStatusInfoDto } from 'interfaces/Chart';
import React from 'react';
import { useQuery } from 'react-query';
import {
  postChartItemsAmountStock,
  postChartStatusStock,
} from 'services/stock-position/grid-stock-position';
import { colors } from 'styles/colors';
import useStyles from './style';

const StockPositionChartsContainer = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  updatedAt,
}: {
  updatedAt: Date | null;
}) => {
  const classes = useStyles();
  const [expandCard, setExpandCard] = React.useState<boolean>(true);
  const [statusChart, setStatusChart] = React.useState<IClusterStatusInfoDto[]>(
    [],
  );

  const dataSeries = React.useMemo(() => {
    return Array.from(
      new Set(
        statusChart
          .map(item => item.status)
          .flat()
          .map(status => status.description),
      ),
    );
  }, [statusChart]);

  const dataChart = React.useMemo(() => {
    const result = statusChart.map(item => {
      const { status, total, cluster } = item;

      const chart = {} as IClusters;

      chart[cluster] = 0;

      status.forEach(value => {
        chart[value.description] = Number(value.total);
      });

      return {
        answer: cluster,
        total,
        ...chart,
      } as unknown as IStatusDataChart & IClusters;
    });

    return result;
  }, [statusChart]);

  const dataChartSort = React.useMemo(
    () => dataChart.sort((a, b) => b.total - a.total).reverse(),
    [dataChart],
  );

  const totalChart = React.useMemo(() => {
    const totalList = statusChart.map(item => item.total);
    const initialValue = 0;
    const total = totalList.reduce((acc, cv) => acc + cv, initialValue);
    return total;
  }, [statusChart]);

  // INÍCIO ESTADO DOS FILTROS
  const [
    installationFilterOptionsContext,
    materialClassFilterOptionsContext,
    groupFilterOptionsContext,
    descriptionItemFilterOptionsContext,
  ] = [
    useSelectFilter('installation'),
    useSelectFilter('material-class'),
    useSelectFilter('group'),
    useSelectFilter('description'),
  ];

  const allFilterContexts = [
    installationFilterOptionsContext,
    materialClassFilterOptionsContext,
    groupFilterOptionsContext,
    descriptionItemFilterOptionsContext,
    updatedAt,
  ];
  // FIM ESTADO DOS FILTROS

  // INÍCIO IDS DOS FILTROS
  const installationIds = installationFilterOptionsContext.map(item =>
    Number(item.value),
  );
  const materialClassIds = materialClassFilterOptionsContext.map(item =>
    Number(item.value),
  );
  const stockGroupIds = groupFilterOptionsContext.map(item =>
    Number(item.value),
  );
  const descriptionItens = descriptionItemFilterOptionsContext.map(
    item => item.label,
  );
  // FIM IDS DOS FILTROS

  const queryStatusChart = useQuery({
    queryKey: ['InspectionStatusContext', allFilterContexts],
    queryFn: async () => {
      const params = {
        installationIds,
        materialClassIds,
        stockGroupIds,
        descriptionItens,
      };

      const { data } = await postChartStatusStock(params);
      return data;
    },
    onSuccess: data => {
      setStatusChart(data.chartInfo);
    },
    enabled: true,
  });

  const queryItemsAmountChart = useQuery({
    queryKey: ['AmountItemsChartContext', allFilterContexts],
    queryFn: async () => {
      const params = {
        installationIds,
        materialClassIds,
        stockGroupIds,
        descriptionItens,
      };

      const { data } = await postChartItemsAmountStock(params);
      return data;
    },
    select: data => {
      const newData = data.chartInfo.map(item => ({
        label: item.label,
        min: item.min,
        current: item.current,
        percentMin: item.percentMin,
        percentCurrent: item.percentCurrent,
      }));

      const limit = Math.max(
        ...data.chartInfo.flatMap(item => [
          item.percentMin,
          item.percentCurrent,
        ]),
      );

      const dividedBy = 10;
      const limitRounded =
        Math.round(limit / dividedBy) * dividedBy + dividedBy;
      const domain = [0, limitRounded];

      const colorScheme = ['#4365A8', '#FF7979'];

      return {
        data: newData,
        clusters: data.clusters,
        total: data.total,
        domain,
        colorScheme,
      };
    },
    enabled: true,
  });

  return (
    <div className={classes.container}>
      <CardAccordion
        title="Posição de Estoque"
        subtitle={(queryItemsAmountChart.data?.clusters || []).join(', ')}
        values={`Total: ${queryItemsAmountChart?.data?.total || 0}`}
        ModalContent={null}
        UpperRightIcon={null}
        backgroundColor={colors.cardAccordionBackgroundDefault}
        clickable={false}
        upperRightIconCallback={() => null}
        expandCard={expandCard}
        setExpandCard={setExpandCard}
        isLoading={
          queryItemsAmountChart?.isLoading || queryItemsAmountChart?.isFetching
        }
      >
        <ChartItemsAmountStock
          data={queryItemsAmountChart.data?.data || []}
          config={{
            colorScheme: queryItemsAmountChart.data?.colorScheme || [],
            domain: queryItemsAmountChart.data?.domain || [],
            series: queryItemsAmountChart.data?.clusters || [],
          }}
        />
      </CardAccordion>

      <CardAccordion
        title="Status de Vencimento"
        subtitle={undefined}
        values={`Total: ${totalChart}`}
        ModalContent={null}
        UpperRightIcon={null}
        backgroundColor={colors.cardAccordionBackgroundDefault}
        clickable={false}
        upperRightIconCallback={() => null}
        expandCard={expandCard}
        setExpandCard={setExpandCard}
        isLoading={queryStatusChart?.isLoading || queryStatusChart?.isFetching}
      >
        <ChartStatusStock data={dataChartSort} dataSeries={dataSeries} />
      </CardAccordion>
    </div>
  );
};

export default StockPositionChartsContainer;
